import { NOOP } from '@lemonade-hq/ts-helpers';
import type { AutoTriggeringOptions, UniclientAction } from '@lemonade-hq/uniclient-chat';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

export interface AutoTriggerActionContextValue {
  readonly registerAutoTriggerAction: (
    action: UniclientAction | undefined,
    performAction: (action?: UniclientAction) => Promise<unknown>,
  ) => void;
  readonly clearAllTimeouts: VoidFunction;
}

export const AutoTriggerActionContext = createContext<AutoTriggerActionContextValue>({
  registerAutoTriggerAction: NOOP,
  clearAllTimeouts: NOOP,
});

interface InvokedActionEntry {
  readonly invoked: true;
}

interface TimeoutEntry {
  readonly timeout: ReturnType<typeof setTimeout>;
}

interface NoInteractionTimeoutEntry extends TimeoutEntry {
  readonly delay: number;
  readonly perform: () => Promise<unknown>;
}

interface TimeoutStorageProps {
  readonly once: Map<string, InvokedActionEntry | TimeoutEntry>;
  readonly noInteractions: Map<string, InvokedActionEntry | NoInteractionTimeoutEntry>;
}

function isInvoked(descriptor: InvokedActionEntry | TimeoutEntry): descriptor is InvokedActionEntry {
  return 'invoked' in descriptor;
}

const INTERACTION_EVENT_TYPES = ['keydown', 'click', 'touchstart'] as const;

export const AutoTriggerActionProvider: FC<PropsWithChildren> = ({ children }) => {
  const timeouts = useRef<TimeoutStorageProps>({
    once: new Map(),
    noInteractions: new Map(),
  });

  const clearAllTimeouts = useCallback(() => {
    [...timeouts.current.once.values(), ...timeouts.current.noInteractions.values()].forEach(descriptor => {
      if (isInvoked(descriptor)) return;

      clearTimeout(descriptor.timeout);
    });
    timeouts.current.once.clear();
    timeouts.current.noInteractions.clear();
  }, []);

  useEffect(() => () => clearAllTimeouts(), [clearAllTimeouts]);

  const handleInteraction = useCallback(() => {
    timeouts.current.noInteractions.forEach((descriptor, key) => {
      if (isInvoked(descriptor)) return;

      const { delay, perform, timeout } = descriptor;

      clearTimeout(timeout);
      const newTimeout = setTimeout(() => {
        void perform();
      }, delay);
      timeouts.current.noInteractions.set(key, { timeout: newTimeout, delay, perform });
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    INTERACTION_EVENT_TYPES.forEach(eventType => {
      document.body.addEventListener(eventType, handleInteraction, { signal: controller.signal });
    });

    return () => controller.abort();
  }, [handleInteraction]);

  const handleActionRegistration = useCallback(
    (
      action: UniclientAction,
      autoTrigger: AutoTriggeringOptions,
      performAction: (action: UniclientAction) => Promise<unknown>,
    ) => {
      const key = JSON.stringify(action);

      const strategy = autoTrigger.strategy ?? 'once';
      if (timeouts.current[strategy].has(key)) return;

      const perform = async (): Promise<unknown> => {
        timeouts.current[strategy].set(key, { invoked: true });
        return await performAction(action);
      };

      const timeout = setTimeout(() => {
        void perform();
      }, autoTrigger.delay);

      if (strategy === 'once') {
        timeouts.current.once.set(key, { timeout });
      } else {
        timeouts.current.noInteractions.set(key, { timeout, delay: autoTrigger.delay, perform });
      }
    },
    [],
  );

  const registerAutoTriggerAction = useCallback(
    (action: UniclientAction | undefined, performAction: (action?: UniclientAction) => Promise<unknown>) => {
      if (action?.autoTrigger !== undefined) {
        const autoTriggerOptions = Array.isArray(action.autoTrigger) ? action.autoTrigger : [action.autoTrigger];
        autoTriggerOptions.forEach(autoTrigger => handleActionRegistration(action, autoTrigger, performAction));
      }
    },
    [handleActionRegistration],
  );

  const value = useMemo(
    () => ({ registerAutoTriggerAction, clearAllTimeouts }),
    [registerAutoTriggerAction, clearAllTimeouts],
  );

  return <AutoTriggerActionContext.Provider value={value}>{children}</AutoTriggerActionContext.Provider>;
};

export function useAutoTriggerActionContext(
  /**
   * If provided, the timeouts will be cleared when the current step index changes. Should be provided only from the main wizard component.
   */
  currentStepIndex?: number,
): Omit<AutoTriggerActionContextValue, 'clearAllTimeouts'> {
  const { registerAutoTriggerAction, clearAllTimeouts } = useContext(AutoTriggerActionContext);

  useEffect(() => {
    clearAllTimeouts();
  }, [clearAllTimeouts, currentStepIndex]);

  return { registerAutoTriggerAction };
}
